import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';

import { SupportedLanguage } from '../../i18n/language.constants';
import { LanguageService } from '../../i18n/language.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-user-menu',
  imports: [TranslocoPipe],
  templateUrl: './user-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserMenuComponent {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('trigger');
  private readonly auth = inject(AuthService);
  protected readonly languageService = inject(LanguageService);

  protected readonly isOpen = signal(false);
  protected readonly isLanguageExpanded = signal(false);

  protected readonly accountLabel = computed(
    () => this.auth.user()?.name || this.auth.user()?.email || null,
  );

  protected toggle(): void {
    this.isOpen.update(open => !open);
    if (!this.isOpen()) {
      this.isLanguageExpanded.set(false);
    }
  }

  protected toggleLanguage(): void {
    this.isLanguageExpanded.update(expanded => !expanded);
  }

  protected selectLanguage(lang: SupportedLanguage): void {
    this.languageService.setLanguage(lang);
    this.close();
  }

  protected logout(): void {
    this.close();
    this.auth.logout();
  }

  @HostListener('document:click', ['$event.target'])
  protected onDocumentClick(target: EventTarget | null): void {
    if (this.isOpen() && !this.elementRef.nativeElement.contains(target as Node | null)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.close();
  }

  private close(): void {
    const wasOpen = this.isOpen();
    this.isOpen.set(false);
    if (wasOpen && this.elementRef.nativeElement.contains(document.activeElement)) {
      this.trigger()?.nativeElement.focus();
    }
  }
}
